import { Button, Flex, Form, Input, Modal, Radio, Space } from 'antd'
import { FC, useState } from 'react'

interface ComputedOutcomeModalProps {
  onClose: () => void
  computedOutcome: boolean
  currentOverride: boolean | undefined
  currentJustification: string | undefined
  onSave: (outcome?: boolean, justification?: string) => void
  title?: string
  passLabel?: string
  failLabel?: string
}

/** Edit an override for the standard computed-section presentation. */
export const ComputedOutcomeModal: FC<ComputedOutcomeModalProps> = ({
  onClose,
  computedOutcome,
  currentOverride,
  currentJustification,
  onSave,
  title = 'Edit Outcome',
  passLabel = 'Pass',
  failLabel = 'Fail',
}) => {
  const [outcome, setOutcome] = useState(currentOverride ?? computedOutcome)
  const [justification, setJustification] = useState(currentJustification ?? '')
  const isOverridden = currentOverride !== undefined
  const hasChanges = outcome !== computedOutcome
  const trimmedJustification = justification.trim()
  const canSave = hasChanges && trimmedJustification !== ''

  const save = () => {
    if (!canSave) return
    onSave(outcome, trimmedJustification)
    onClose()
  }

  const reset = () => {
    onSave()
    onClose()
  }

  return (
    <Modal
      open
      title={title}
      onCancel={onClose}
      onOk={save}
      okText={isOverridden ? 'Save changes' : 'Apply override'}
      okButtonProps={{ disabled: !canSave }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Flex justify='space-between'>
          <Button onClick={reset} disabled={!isOverridden}>
            Reset to computed
          </Button>
          <Space>
            <CancelBtn />
            <OkBtn />
          </Space>
        </Flex>
      )}>
      <Form layout='vertical'>
        <Form.Item label='Outcome'>
          <Radio.Group
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}>
            <Radio value={true}>{passLabel}</Radio>
            <Radio value={false}>{failLabel}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label='Justification'
          required={hasChanges}
          validateStatus={hasChanges && !trimmedJustification ? 'error' : ''}
          help={
            hasChanges && !trimmedJustification
              ? 'Explain why the computed outcome should be overridden.'
              : undefined
          }>
          <Input.TextArea
            rows={4}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
